package pl.zar.tikthrow.viewmodel

import kotlin.time.Clock
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.flow.update
import pl.zar.tikthrow.shared.domain.Platform
import pl.zar.tikthrow.shared.domain.SocialMediaAccount

object AccountsStore {
    private val _accounts = MutableStateFlow<List<SocialMediaAccount>>(emptyList())
    val accounts: StateFlow<List<SocialMediaAccount>> = _accounts.asStateFlow()

    fun addAccount(platform: Platform, handle: String, displayName: String?) {
        val trimmedHandle = handle.trim()
        if (trimmedHandle.isBlank()) return
        val now = Clock.System.now()
        val account = SocialMediaAccount(
            id = "${platform.name}_${trimmedHandle}_${now.toEpochMilliseconds()}",
            platform = platform,
            accountHandle = trimmedHandle,
            displayName = displayName?.trim()?.takeIf { it.isNotBlank() },
            avatarUrl = null,
            isActive = true,
            createdAt = now,
            updatedAt = now
        )
        _accounts.update { current ->
            if (current.any { it.platform == platform && it.accountHandle == trimmedHandle }) current
            else current + account
        }
    }

    fun setYouTubeAccounts(youtubeAccounts: List<SocialMediaAccount>) {
        _accounts.update { current ->
            current.filterNot { it.platform == Platform.YOUTUBE } + youtubeAccounts
        }
    }

    fun markYouTubeConnected() {
        // temporary optimistic state until refreshYouTubeStatus() fetches the real list
        if (_accounts.value.none { it.platform == Platform.YOUTUBE }) {
            val now = Clock.System.now()
            val placeholder = SocialMediaAccount(
                id = "${Platform.YOUTUBE.name}_pending",
                platform = Platform.YOUTUBE,
                accountHandle = "pending",
                displayName = "YouTube łączy...",
                avatarUrl = null,
                isActive = true,
                createdAt = now,
                updatedAt = now
            )
            _accounts.update { current -> current + placeholder }
        }
    }

    fun markYouTubeDisconnected() {
        _accounts.update { current -> current.filterNot { it.platform == Platform.YOUTUBE } }
    }

    fun setTikTokAccounts(tikTokAccounts: List<SocialMediaAccount>) {
        _accounts.update { current ->
            current.filterNot { it.platform == Platform.TIKTOK } + tikTokAccounts
        }
    }

    fun setFacebookAccounts(facebookAccounts: List<SocialMediaAccount>) {
        _accounts.update { current ->
            current.filterNot { it.platform == Platform.FACEBOOK } + facebookAccounts
        }
    }

    fun setInstagramAccounts(instagramAccounts: List<SocialMediaAccount>) {
        _accounts.update { current ->
            current.filterNot { it.platform == Platform.INSTAGRAM } + instagramAccounts
        }
    }

    fun markTikTokConnected() {
        if (_accounts.value.none { it.platform == Platform.TIKTOK }) {
            val now = Clock.System.now()
            val placeholder = SocialMediaAccount(
                id = "${Platform.TIKTOK.name}_pending",
                platform = Platform.TIKTOK,
                accountHandle = "pending",
                displayName = "TikTok łączy...",
                avatarUrl = null,
                isActive = true,
                createdAt = now,
                updatedAt = now
            )
            _accounts.update { current -> current + placeholder }
        }
    }

    fun markTikTokDisconnected() {
        _accounts.update { current -> current.filterNot { it.platform == Platform.TIKTOK } }
    }

    fun markFacebookDisconnected() {
        _accounts.update { current -> current.filterNot { it.platform == Platform.FACEBOOK } }
    }

    fun markInstagramDisconnected() {
        _accounts.update { current -> current.filterNot { it.platform == Platform.INSTAGRAM } }
    }

    fun removeAccount(id: String) {
        _accounts.update { it.filter { acc -> acc.id != id } }
    }
}
